import mainVueMixin from '@/mixins/mainVueMixin'
import { STORE_OPTIONS_API, findStoreOption, storeOptionsOf, storeOptionsQuery } from '@/utils/storeOptions'
import { SILENT_READ_CONFIG } from '@/utils/scopeTree'
import { STORE_PICKER_TEXT } from '@/utils/scopeTreeText'
import { STORE_ORIGIN, storeReadFailureNotice } from '@/utils/storeSaveTarget'
import { latestGuard } from '@/utils/latestGuard'

/**
 * **사람이 방금 고른 매장** — 값은 이미 바뀌었고 폼은 아직 옛 매장의 것이다.
 *
 * 그래서 읽는 동안 **저장을 잠그고**, 못 읽으면 **값을 되돌려**([revertStoreSelection]) 다시 고르라고 한다.
 */
const PICKED = Object.freeze({
  lockSave: true,
  revert: true,
  notice: () => ({ type: 'warning', message: STORE_PICKER_TEXT.optionLoadFailed }),
})

/**
 * **폼을 채우는 매장** — `add()` 의 검색 조건 씨앗과 `up()` 의 기존 행이다.
 *
 * 사람이 고른 것이 아니므로 **되돌릴 선택이 없다**: 실패해도 값은 그대로 두고 언어 탭이 안 선 것만
 * 말한다. 되돌리면 수정 폼에서 **볼 수도 고칠 수도 없는 매장**이 비워진다 — 그 자리에는 선택 칸이
 * 아예 렌더되지 않아(`v-if="'I' === inputForm.statu"`) 다시 고를 길이 없고, 저장은 빈 코드로 나간다.
 *
 * 저장도 잠그지 않는다: 값이 바뀌지 않으므로 **남의 매장 데이터가 섞이지 않는다.** 아직 덜 선 폼으로
 * 저장하면(`add()` 직후의 빈 다국어 칸) 서버가 400 으로 답하고 **그것이 옳은 답**이다 — 잘못된 데이터가
 * 쓰이는 갈래가 아니다. 게다가 `add()`·`up()` 이 곧바로 이어서 버튼을 여는 자리라 잠가도 뜻이 없다.
 */
const SEEDED = Object.freeze({
  lockSave: false,
  revert: false,
  notice: () => ({ type: 'warning', message: STORE_PICKER_TEXT.optionSeedFailed }),
})

/**
 * **주소가 정한 화면의 매장** — 쿼리 `storeCd` 가 화면 전체(목록·언어 축·등록 대상)의 매장을 정한다(할인 화면의
 * `?storeCd=` — 대행 띠의 바로가기·북마크).
 *
 * 폼 한 칸을 채우는 [SEEDED] 가 아니다: 못 읽은 매장을 선택기에 남기면 화면은 목록도 버튼도 없이 멈추고, 씨앗의
 * 문구(「다국어 입력이 보이지 않을 수 있다」)는 실제와 다르다(R-H 2차 리뷰). 그래서 사람이 고른 것([PICKED])과
 * 같이 **되돌린다**([revertStoreSelection] — 진입 때는 확정 매장이 없으므로 비운다). 다른 것은 문구뿐이다 — 사람이
 * 고른 적이 없으니 「다시 고르라」가 아니라 **주소의 매장을 열 수 없다는 사실**과 할 일(고르기)을 말한다. 문구는 매장 관리의
 * `?storeCd=` 진입과 같은 판정([storeReadFailureNotice] — 확실한 거절 / 그 밖의 실패, 리뷰 L2·N2)이 고른다.
 */
const ROUTED = Object.freeze({
  lockSave: true,
  revert: true,
  notice: (error) => storeReadFailureNotice(STORE_ORIGIN.ROUTED, error),
})

/**
 * **고른 매장의 셀렉트 메타데이터를 답하는 한 곳** — 다국어 축(`defaultLan`·`lans`)과 국제결제
 * (`internationalPayYn`)가 필요한 등록·수정 폼 다섯이 함께 쓴다.
 *
 * 왜 생겼나: 종전에는 다섯 화면이 전부 **후보 배열**(`GET /store/combo`)에서만 그 칸을 찾았다.
 * 범위 트리가 서기 전에는 그 계정이 고를 수 있는 매장 = 후보 배열이라 참이었는데, 창이 트리를 주면서
 * **후보에 없는 매장**을 고를 수 있게 됐다. 그런 매장을 고르면 언어 탭이 서지 않고, 저장이 400 이
 * 되거나 이전 매장의 언어 설정이 그대로 남는다. 새로 연 경로라 종전에는 드러날 수 없던 구멍이다.
 *
 * **도달 범위**(이 파일이 정본이다): 이 경로는 **후보 목록과 트리가 어긋나는 계정**에서만 열린다.
 * 다섯 화면의 목록 API 권한은 `custMng`·`custMngTemp`·`storeMng` 뿐이고(내부 `admin`·`mng` 는 메뉴에
 * 없으며 URL 로 들어가면 첫 목록 조회의 403 에서 로그아웃된다 — 기존 정책), `custMng`·`storeMng` 의
 * 후보 목록은 트리와 같은 집합이라 상세 조회가 일어나지 않는다. 실제로 열리는 계정은 `custMngTemp`
 * 하나다(리얼 0건). **그래도 계약을 틀린 채로 두지 않는다** — 수정이 작다.
 *
 * 답하는 순서는 둘이고 **뜻이 다르다**:
 *
 * 1. **후보 목록** — 이미 손에 있다. 부르지 않는다(사장님·사업장 계정의 평소 경로가 이것이다).
 * 2. **셀렉트 한 줄**(`GET /store/combo?storeCd=`, R-D2 S-1) — 후보에 없는 매장. 후보 목록과 **같은
 *    조회**에 매장 코드 하나를 더한 것이라 행 모양이 같고 접을 것이 없다. 스코프는 서버가 본다(후보 목록과
 *    같은 축) — 화면이 「이 계정이 볼 수 있나」를 다시 판정하지 않는다. 종전에는 이 한 줄을 위해 매장 상세
 *    (`GET /store/{storeCd}`) 전체를 받았고 거기에 PG 상점키가 실려 있었다.
 *
 *    응답에서 행을 고르는 것은 **첫 행이 아니라 그 매장 코드의 행**이다([findStoreOption]). `storeCd` 를 모르는
 *    옛 서버는 조건을 버리고 후보 전부를 주는데, 첫 행을 쓰면 **다른 매장의 언어 축**이 조용히 폼에 선다.
 *    코드로 고르면 그 서버에서는 「못 얻음」이 되어 아래 실패 갈래로 간다.
 *
 * ## 갈래는 **이름 셋**이고, 다른 것은 실패의 행동뿐이다
 *
 * 매장을 적용하는 자리의 뜻은 셋이다 — 사람이 `@change` 로 고른 것([resolveStoreOption]), 폼을 채우는 것
 * ([seedStoreOption]: `add()` 의 씨앗·`up()` 의 기존 행), 주소가 화면 전체의 매장을 정한 것([routeStoreOption]:
 * 할인 화면의 `?storeCd=`). 해석기가 앞의 둘을 구분할 재료를 갖지 못했던 동안, 수정 폼의 조회 한 번이 실패하면
 * **손대지도 않은 매장이 비워졌다.** 주소의 매장을 씨앗으로 적용하던 동안에는 그 반대로, 못 읽은 매장이 화면을
 * 멈춘 채 남았다. 플래그 인자가 아니라 이름인 이유: 호출 자리가 이미 답을 알고 있고(그 자리가 `@change` 인가
 * `add`/`up` 인가 진입인가), 이름이면 부르는 쪽에서 뜻이 읽힌다.
 *
 * | 갈래 | 저장 잠금 | 실패 시 되돌림 | 실패 문구 |
 * |---|---|---|---|
 * | [PICKED] 사람이 고름 | 읽는 동안 | 있음 | 다시 고르세요 |
 * | [SEEDED] 폼을 채움 | 없음 | 없음(값 유지) | 언어 탭이 안 섰을 수 있다 |
 * | [ROUTED] 주소가 정함 | 읽는 동안 | 있음 | 주소의 매장을 열 수 없다 — 고르세요 (확실한 거절이 아니면 일반 오류 안내) |
 *
 * 조회·순번·낡음 판정은 **하나**([readStoreOption])가 갖고 갈래는 실패의 행동만 고른다.
 *
 * ## 소비자 계약: 화면은 `applyStoreOption(storeCd, pending)` **하나만** 구현한다
 *
 * 진입점([storeInputSelect]·[storeSeedSelect]·[storeRouteSelect])은 이 믹스인이 갖는다 — 다섯 화면에서 주석까지
 * 글자 그대로 같았고, 다른 것은 답을 폼에 적는 본문뿐이었다. 화면은 그 본문을 `applyStoreOption` 으로
 * 구현하고 `@change` 와 `add()`·`up()`·진입은 여기 이름을 부른다. 믹스인의 `methods` 는 컴포넌트가
 * 덮어쓸 수 있으므로 예외가 필요한 화면이 생겨도 막히지 않는다.
 *
 * ## `null` 은 세 가지 뜻이고, **가르는 일은 여기서 한다**
 *
 * 호출부(다섯 화면)는 `if (!option) return` 한 줄뿐이다. 그 뒤에 무엇이 필요한지는 이 믹스인이 안다:
 *
 * - **낡음** — 그 사이 다른 매장을 골랐다. 아무것도 하지 않는다(값을 되돌리면 방금 고른 매장을 지운다).
 *   **저장 잠금도 풀지 않는다** — 최신 요청의 잠금을 옛 응답이 푸는 것이라서다.
 * - **실패** — 읽지 못했다. 갈래가 정한 행동을 한다.
 * - **그 행이 없음** — 200 인데 그 매장의 행이 없다: 범위 밖·없는 매장(서버 계약상 **빈 배열**), 게이트웨이가 준
 *   HTML·빈 200, `storeCd` 를 모르는 옛 서버. 실패와 **같은 답**이다: 화면이 침묵하면 폼은 옛 언어 탭을 든 채
 *   새 매장 코드를 갖게 되고 아무도 그것을 모른다.
 *
 * 범위 밖의 답은 404 도 403 도 아닌 **빈 배열**이다(셀렉트의 「없음」). `logoutOn403:false` 는 그래서 이 경로에서
 * 걸리는 일이 없지만 방어로 남긴다. **「서버가 주지 않았다」 갈래끼리는 가르지 않는다** — 빈 배열·404·403 을 화면이 갈라
 * 말하면 매장 코드 열거의 신호가 된다. 다만 [ROUTED] 는 **확실한 거절이 아닌 실패**(일시적 4xx·401·5xx·전송 실패·코드
 * 오류)를 일반 오류 안내로 따로 말한다 — 「없거나 범위 밖일 수 있다」가 틀리고 새로고침·다시 로그인으로 풀릴 수 있어서다
 * (리뷰 L2·N2 — 판정은 `storeReadFailureNotice` 한 곳).
 * 인터셉터의 기본 토스트는 끄고(`showErrorMessage:false`) 갈래가 정한 안내 하나만 띄운다.
 *
 * ## 실패의 되돌리기 계약: **선택 모델은 화면이 안다** — [revertStoreSelection]
 *
 * [PICKED] 가 실패하면 사람의 선택을 거둔다. 그런데 **선택이 사는 자리**는 화면마다 다르고, 무엇으로 되돌릴지는
 * 그 자리가 무엇을 소유하는가로 갈린다. 그래서 믹스인은 「언제·무엇을 재료로」만 정하고 「어디를·어떻게」는
 * 화면이 [revertStoreSelection] 으로 답한다(믹스인 `methods` 는 컴포넌트가 덮어쓴다):
 *
 * - **등록·수정 폼 다섯**(카테고리·메뉴·옵션·옵션 그룹·외부 코드) — 선택은 폼의 한 칸(`inputForm.store.storeCd`)이고
 *   저장 본문 한 칸일 뿐이다. **비운다**(기본 구현 — 다시 고르라는 안내와 함께). 빈 코드 저장은 서버 400 이고
 *   그것이 옳은 답이다. `store-picker-form-meta` 가 이 동작을 고정한다.
 * - **화면 전체의 매장**(할인 — 최상위 `storeCd`, 사람의 선택과 주소의 매장 둘 다) — 선택이 목록·언어 축·등록 대상을 **함께** 소유한다. 비우면
 *   앞 매장의 목록·언어 축이 주인 없이 남고, 그대로 두면 앞 매장의 언어 축으로 새 매장에 등록된다. 그래서
 *   **마지막 확정 매장**([confirmedStoreOption] — 그 행)으로 돌아가 그 행으로 화면 컨텍스트를 다시 세운다.
 *
 * **확정 매장**은 메타데이터를 얻어 화면에 넘긴 **최신 회차**의 셀렉트 행이다(후보에서 답했든 한 줄을 읽었든).
 * 코드가 아니라 **행**을 남기는 이유: 후보 밖 매장은 후보 배열에 없어, 코드만으로는 그 매장의 언어 축을 다시 얻을
 * 수 없다. 낡은 회차·실패한 회차는 확정하지 않는다.
 */
export default {
  mixins: [mainVueMixin],

  data() {
    return {
      /** 「이 화면이 방금 물은 매장」 축 하나 — 늦게 온 상세가 새 선택을 덮지 않게 */
      storeOptionGuard: latestGuard(),
      /** 마지막 확정 매장의 셀렉트 행 — 메타데이터를 얻어 화면에 넘긴 최신 회차. 되돌리기의 재료다(머리말) */
      confirmedStoreOption: null,
    }
  },

  // 떠나는 화면의 한 줄 조회는 버린다 — 늦게 온 실패가 다른 화면에 「다시 고르세요」를 띄우거나 죽은 폼을 되돌리지 않게.
  // **닫는다**: 떠난 뒤 도착한 매장 후보 콜백이 한 줄 조회를 새로 시작해도 그 회차는 최신이 아니다(`utils/latestGuard`)
  beforeDestroy() {
    this.storeOptionGuard.close()
  },

  methods: {
    /**
     * **사람이 골랐다**(`@change` 가 부른다) — 못 얻으면 그 선택을 거둔다(값 되돌리기 + 다시 고르라는 안내).
     *
     * 화면의 `@change` 가 곧바로 이것을 가리킨다. 다섯 화면에 글자 그대로 같은 사본이 있던 것을
     * 여기로 접었다 — 다른 것은 [applyStoreOption] 본문뿐이었다.
     */
    storeInputSelect(storeCd) {
      return this.applyStoreOption(storeCd, this.resolveStoreOption(storeCd))
    },

    /**
     * **폼을 채운다**(`add()` 의 씨앗 · `up()` 의 기존 행) — 사람이 고른 것이 아니므로 되돌릴 선택이
     * 없다. 못 얻어도 값은 그대로 두고 언어 탭이 안 선 것만 말한다.
     */
    storeSeedSelect(storeCd) {
      return this.applyStoreOption(storeCd, this.seedStoreOption(storeCd))
    },

    /**
     * **주소가 화면의 매장을 정했다**(진입의 쿼리 `storeCd`) — 못 얻으면 사람이 고른 것처럼 되돌리고, 주소의 매장을
     * 열 수 없다고 말한다([ROUTED]).
     */
    storeRouteSelect(storeCd) {
      return this.applyStoreOption(storeCd, this.routeStoreOption(storeCd))
    },

    /** 사람이 `@change` 로 고른 매장 — 실패면 값을 되돌리고 다시 고르라고 한다 */
    resolveStoreOption(storeCd) {
      return this.readStoreOption(storeCd, PICKED)
    },

    /** 폼을 채우는 매장(`add()` 의 씨앗·`up()` 의 기존 행) — 실패해도 값은 그대로다 */
    seedStoreOption(storeCd) {
      return this.readStoreOption(storeCd, SEEDED)
    },

    /** 주소가 정한 화면의 매장 — 실패면 값을 되돌리고 주소의 매장을 열 수 없다고 한다 */
    routeStoreOption(storeCd) {
      return this.readStoreOption(storeCd, ROUTED)
    },

    /**
     * 그 매장의 셀렉트 행 — 후보에 있으면 그것, 없으면 셀렉트 한 줄을 읽는다.
     *
     * **언제나 Promise** 이고 **모든 갈래가 먼저 순번을 올린다**(후보에 있는 갈래도). 후보 갈래가
     * 순번을 건너뛰면, 앞서 나간 상세 조회가 여전히 최신이라 믿고 나중에 그 폼을 덮는다.
     *
     * ## 저장 잠금의 계약: **최신이 된 회차가 언제나 「기다릴 것이 있는가」를 말한다**
     *
     * 잠그는 자리만 말하게 두면 비대칭이 생긴다 — 상세를 읽는 중(잠김)에 **후보에 있는** 매장을 고르면
     * 새 회차는 순번만 올리고 즉시 돌아가고, 앞 회차의 응답은 이미 낡아 스스로 풀지 못한다. 폼은
     * 정상인데 저장 버튼만 영영 잠긴 채 남는다(취소하고 다시 열어야 풀린다).
     *
     * 그래서 **시작에서 이 회차가 필요한 상태를 쓰고, 끝에서 「이제 기다릴 것이 없다」를 쓴다.**
     * 앞 회차의 잠금은 그 순간 이 회차가 인계받는다. 낡은 응답은 여전히 아무것도 쓰지 않는다 —
     * 최신이 아니므로 말할 자격이 없다.
     *
     * @param {number|string} storeCd
     * @param {{lockSave: boolean, revert: boolean, notice: function(*): {type: string, message: string}}} intent [PICKED]·[SEEDED]·[ROUTED]
     * @returns {Promise<object|null>} 후보 행 모양, 낡았거나 못 얻으면 `null`
     */
    readStoreOption(storeCd, intent) {
      const seq = this.storeOptionGuard.begin()
      const known = findStoreOption(this.store, storeCd)
      const pending = !known && storeCd != null && storeCd !== ''

      this.lockStoreOptionSave(pending && intent.lockSave)

      // 기다릴 것이 없다 — 후보에서 바로 답하거나, 물을 매장이 없다
      if (!pending) {
        if (known) this.confirmedStoreOption = known

        return Promise.resolve(known || null)
      }

      return new Promise((resolve) => {
        /** 성공·실패·그 행 없음이 **한 자리**로 모인다 — 낡은 회차는 여기서 조용히 끝난다. `error` 는 실패일 때만 있다 */
        const settle = function (option, error) {
          if (!this.storeOptionGuard.isLatest(seq)) return resolve(null)
          this.lockStoreOptionSave(false)
          if (option) this.confirmedStoreOption = option
          else this.onStoreOptionMissing(intent, error)

          resolve(option)
        }.bind(this)

        this.mainVue.$axios
          .search(
            STORE_OPTIONS_API,
            storeOptionsQuery({ storeCd }),
            function (result) {
              settle(findStoreOption(storeOptionsOf(result), storeCd))
            },
            SILENT_READ_CONFIG
          )
          // 토스트를 끈 조회다 — 실패를 값(`ok:false`)으로 받아 이 자리가 말한다. 거절(그 밖의 오류)도 같은 자리로 모은다
          .then(function (outcome) {
            if (!outcome.ok) settle(null, outcome.error)
          })
          .catch(function (error) {
            settle(null, error)
          })
      })
    },

    /**
     * 읽는 동안은 **저장할 수 없다** — 값은 새 매장인데 폼은 옛 매장의 다국어 행을 들고 있다.
     *
     * 전역 마스크를 끈 뒤로 그 구간이 눌리는 자리가 됐다(`save()` 는 조회 완료를 기다리지 않는다).
     * 다섯 화면이 전부 `btn.saveDisabled` 로 저장 버튼을 잠그므로 화면마다 `:disabled` 를 덧대지 않는다.
     * 할인 화면은 `btn` 을 들지 않는다 — 읽는 동안은 확정 컨텍스트가 비어 저장 가능 여부가 거기서 이미 거짓이다.
     */
    lockStoreOptionSave(locked) {
      if (this.btn) this.btn.saveDisabled = locked
    },

    /**
     * 메타데이터를 못 얻었다 — 갈래가 정한 대로 되돌리고, 사람이 알아야 할 것을 한 번 말한다. 문구는 갈래가 실패(`error` —
     * 그 행이 없으면 `undefined`)에서 고른다.
     */
    onStoreOptionMissing(intent, error) {
      if (intent.revert) this.revertStoreSelection(this.confirmedStoreOption)
      const notice = intent.notice(error)
      this.mainVue.$message({ type: notice.type, message: this.$t(notice.message) })
    },

    /**
     * 사람의 선택을 거둔다([PICKED] 실패) — **선택 모델을 가진 화면이 답하는 자리**다(머리말 「되돌리기 계약」).
     *
     * 기본 구현은 등록·수정 폼 다섯의 것이다: 폼의 매장 칸(`inputForm.store.storeCd`)을 비운다. 선택이 화면 전체의
     * 매장이라 목록·언어 축을 함께 소유하는 화면(할인)은 이 메서드를 덮어 그 행으로 돌아간다.
     *
     * @param {?object} confirmedStoreOption 마지막 확정 매장의 셀렉트 행 — 아직 없으면 `null`
     */
    revertStoreSelection(confirmedStoreOption) {
      if (this.inputForm && this.inputForm.store) this.inputForm.store.storeCd = null
    },
  },
}
